import { Injectable } from '@angular/core';
import { FormActionHandlerService } from './base.service';
import { SwalService } from '../../../fastlane-common/services/swal.service';
import { Router } from '@angular/router';
import { FormShareService } from '../share.service';
import { NavService } from '../../../fastlane-common/services/nav.service';
import { Payment } from '../../../fiscal/models/payment.model';
import { PaymentProject } from '../../../fiscal/models/payment.project.model';
import { environment } from 'environments/environment';
import { FormDataService } from '../data.service';
import { FormFeeService } from '../fee.service';
import { UserContextService } from '../../../user/services/user-context.service';
import { getUserModule } from '../../../user/user.functions';
import { oneToOneFormTypes } from '../../form.constants';
import { formTypes } from 'app/form/form.constants';
import { BalanceAdjustmentRequest } from '../../../project/models/balance-adjustment-request.model';
import { FormIdentifier } from '../../../project/models/form-identifier.model';
import { Subject } from 'rxjs';
import {feeTypes} from '../../../fiscal/fiscal.constants';
import {PrrFormTypes} from '../../../project/project.constants';
import { ProjectDataService } from '../../../project/services/project-data.service';
import { ProjectDetailService } from '../../../project/services/project-shared.service';
import { refreshResolvedProject } from '../../../project/resolvers/project.resolver';

@Injectable()
export class PayFormActionHandlerService extends FormActionHandlerService {
  // Current payment
  newPayment: Subject<Payment> = new Subject();

  constructor(
    private dataService: FormDataService,
    private navService: NavService,
    private router: Router,
    private shareService: FormShareService,
    private swal: SwalService,
    private userContext: UserContextService,
    private feeService: FormFeeService,
    private projectDataService: ProjectDataService,
    private projectDetailService: ProjectDetailService
  ) {
    super();
  }

  exec(): Promise<any> {
    const that = this;
    return new Promise((resolve, reject) => {

      const loadPayment = () => {
        that.swal.load(
          'Getting things ready...',
          'You will be redirected to our payment page once everything is ready.'
        );

        const failedFormPayments = that.shareService.payments.filter(
          payment => !payment.confirmationNumber
        );

        if (failedFormPayments.length > 0) {
          // Reuse a failed payment if one failed prior to this call
          const failedFormPayment = failedFormPayments[0];

          that.swal
            .selfDestructIn(
              { title: 'Redirecting in...' },
              () => (window.location.href = failedFormPayment.requestUrl) // Redirect user to paypoint
            )
            .then(resolve)
            .catch(resolve);
        } else {
          that.swal.dismiss();
          // Prepare new payment
          const newPayment = that.createNewPaymentTransaction();

          // If non production, bypass going to payment for easier testing
          if (this.shouldBypassPaypoint) {
            // Update total paid amount to be same as amount due
            newPayment.totalPaidAmount = this.shareService.form.fees[0].amountDue;
            newPayment.paymentProject.paidAmount = this.shareService.form.fees[0].amountDue;
            that.dataService.fakePayment(newPayment).subscribe(
              payment => {
                refreshResolvedProject(
                  that.shareService.projectBase.id,
                  that.projectDataService,
                  that.projectDetailService
                ).subscribe(
                  () => {
                    const form = that.shareService.form;
                    if (!form.payments) {
                      form.payments = [payment.id];
                    } else if (form.payments.indexOf(payment.id) === -1) {
                      form.payments.push(payment.id);
                    }
                    that.feeService.recalculateFees();
                    that.dataService.saveForm().subscribe(
                      () => resolve(),
                      () =>
                        reject(
                          'An error occurred while saving the form after the simulated payment.'
                        )
                    );
                  },
                  () =>
                    reject(
                      'An error occurred while refreshing the project after the simulated payment.'
                    )
                );
              },
              error =>
                reject(
                  'An error occurred while attempting to fake the payment process. Please try again later.'
                )
            );

            return;
          }

          this.shareService.form.modifiedDate = new Date(Date.now());
          // Proceed for production environments
          that.dataService.createPayment(newPayment).subscribe(
            payment => {
              that.newPayment.next(payment);
            },
            error => {
              console.log(error);
              reject(
                'An error occurred while attempting to initiate the payment process. Please try again later.'
              );
            }
          );
        }
      }


      if (that.shareService.form.type === formTypes.prr.abbrev) {
        loadPayment();
      } else {
        that.swal
          .yesOrNo({
            title: 'Are you ready to submit and pay this form?',
            text:
              'To pay for this form, you will be brought to a pay component that interacts with US Bank. ' +
              'Please do not refresh or close the page -- wait until the payment screen fully loads.'
          }).then(() => {
          loadPayment()
        }).catch(resolve);
      }

    });
  }

  addPaypointParams(payment: Payment) {
    const queryString = `?c=2&o=${payment.confirmationNumber}&t=${
      payment.totalPaidAmount
    }&d=${new Date().toISOString()}&hash=${payment.hash}&ct=${
      payment.paymentMethod
    }&i=${payment.id}`;

    return queryString;
  }

  private get shouldBypassPaypoint() {
    // Simulate the payment ONLY on a local dev machine. Deployed environments
    // have a working payment provider (a test provider in dev, the real one in
    // prod) and must take the real pay flow; payments simply can't be processed
    // from localhost, so that is the only place the pay flow is bypassed. Gated
    // purely on the host, so no deployed environment can ever skip a payment
    // (note environment.shouldBypassPaypoint is true on deployed dev too, which
    // is why it can't be the gate).
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host === '::1';
  }

  /**
   * @summary Used to create a new payment transaction which will save
   * initial details that can be used to obtain a payment.id or reference to be sent with
   * all of the requests to Orbipay
   */
  private createNewPaymentTransaction(): Payment {
    const that = this;

    // Create a new payment object here, and then in the DB, and then give that new payment object to PayPoint
    const project = this.shareService.project;
    const formId = this.shareService.formId;
    const form = project.getProjectForm(formId);
    let feeType = formId.formType.toUpperCase();

    // If form type is balance adjustment request, the fee type is the drop down option which the manager selects
    if (
      this.shareService.form.type === formTypes.balanceAdjustmentRequest.abbrev
    ) {
      const bar = <BalanceAdjustmentRequest>form;
      feeType = bar.adjustmentSubtype;
    } else if ( this.shareService.form.type === formTypes.coc.abbrev) {
      feeType = feeTypes.acr.upperAbbrev; // todo this needs to be changed to acrite once 294 is merged in
    }

    // Request creation of new payment in the payments collection from API
    const newPayment = <Payment>{
      createDate: new Date(),
      paymentProject: <PaymentProject>{
        projectId: project.projectInfo.projectId,
        projectStatus: null,
        incentiveProgram: project.projectInfo.incentiveProgram,
        paidAmount: 0, // sending 0 because at this state the user has not paid anything.
        formId: formId,
        feeType: feeType
      },
      totalPaidAmount: 0 // sending 0 because at this state the user has not paid anything.
    };
    console.log(newPayment);

    return newPayment;
  }

  private getFormPrePaymentConfiguration() {
    // Create a new payment object here, and then in the DB, and then give that new payment object to PayPoint
    const project = this.shareService.project;
    const formId = this.shareService.formId;
    const form = project.getProjectForm(formId);

    let absoluteReturnUrlString = this.navService.convertUrlTreeToString([]);
    let feeType = formId.formType.toUpperCase(); // Making form type in an uppercase fashion be the value displayed to user.

    if (oneToOneFormTypes.includes(formId.formType)) {
      // Convert return url tree to url string
      absoluteReturnUrlString = this.navService.convertUrlTreeToString([
        getUserModule(this.userContext.currentUser.userType),
        'projects',
        formId.projectGuid,
        formId.formType
      ]);
    } else {
      // Convert return url tree to url string
      absoluteReturnUrlString = this.navService.convertUrlTreeToString([
        getUserModule(this.userContext.currentUser.userType),
        'projects',
        formId.projectGuid,
        `${formId.formIndex}`,
        formId.formType
      ]);
    }

    // If form type is balance adjustment request, the fee type is the drop down option which the manager selects
    if (
      this.shareService.form.type === formTypes.balanceAdjustmentRequest.abbrev
    ) {
      const bar = <BalanceAdjustmentRequest>form;
      feeType = bar.adjustmentSubtype;
    }

    // Generate PayPoint Url
    const requestUrl = this.getPaypointUrl(
      absoluteReturnUrlString,
      form.fees[0].amountDue,
      '{{paymentGuid}}',
      project.projectInfo.companyName,
      project.projectInfo.projectName,
      project.projectInfo.incentiveProgram
    );

    // Request creation of new payment in the payments collection from API
    return <Payment>{
      createDate: new Date(),
      paymentProject: <PaymentProject>{
        projectId: project.projectInfo.projectId,
        projectStatus: null,
        incentiveProgram: project.projectInfo.incentiveProgram,
        paidAmount: 0, // sending 0 because at this state the user has not paid anything.
        formId: formId,
        feeType: feeType
      },
      requestUrl: requestUrl,
      totalPaidAmount: 0 // sending 0 because at this state the user has not paid anything.
    };
  }

  private getPaypointUrl(
    unencodedReturnUrl: string,
    amount: number,
    paymentId: string,
    companyName: string,
    projectName: string,
    incentiveProgram: string
  ) {
    const encodedReturnUrl = encodeURIComponent(unencodedReturnUrl);
    const requestUrl = environment.paypointUrl;

    // Encoded this one separately to be used when setting the queryParameter "id"
    const encodedPaymentIdPlaceHolder = encodeURIComponent(paymentId);

    // * PayPoint has a bug where it cannot process an amount that does not follow a specific format.
    // No additional format required if amount is an integer
    // If amount has decimals, it requires exactly 2 decimal places => that's what this following code is for
    const formattedAmount =
      amount
        .toString()
        .split('.')
        .pop().length < 2
        ? `${amount}0`
        : amount;

    const customParams = `${paymentId},"${companyName}","${projectName}",${incentiveProgram},${formattedAmount}`;
    const encodedCustomParams = encodeURIComponent(customParams);

    // All of query string Parameters to be appended to Paypoint Request Url
    const requestParameters =
      `returnUrl=${encodedReturnUrl}&amount=${formattedAmount}` +
      `&id=${encodedPaymentIdPlaceHolder}&custom=${encodedCustomParams}`;

    return requestUrl + requestParameters;
  }

  private simulateSuccessfulPayment(formId: FormIdentifier, payment: Payment) {
    // Create the return url
    const url = this.navService.convertUrlTreeToString([
      getUserModule(this.userContext.currentUser.userType),
      'projects',
      formId.projectGuid,
      `${formId.formIndex}`,
      formId.formType
    ]);

    // Append successful codes
  }
}
